import { useModoConsultaEvaluacion } from '../../../../hooks/useModoConsultaEvaluacion';
import React, { useState, useContext } from 'react';
import { AppContext } from '../../../../Context';
import { collection, addDoc } from "firebase/firestore";
import { db } from '../../../../firebase/firebase';
import { Container, Form, Button, FormGroup, FormControl, Row, Col, FormLabel } from 'react-bootstrap';
import { DndContext, DragOverlay, MouseSensor, TouchSensor, KeyboardSensor, useSensor, useSensors,
    useDraggable, useDroppable, pointerWithin, rectIntersection, closestCenter } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy, useSortable, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { createPortal } from 'react-dom';
import Swal from 'sweetalert2';
import './M7_Ejercicio7.scss';

const useDragSensors = (ordenar = false) => useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
    useSensor(KeyboardSensor, ordenar ? { coordinateGetter: sortableKeyboardCoordinates } : {})
);

const DraggableCause = ({ item, selected, onSelect }) => {
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, isDragging } = useDraggable({ id: item.id });
    return <div ref={setNodeRef} className={`causa-efecto__causa ${selected ? 'seleccionada' : ''}`} style={{ opacity: isDragging ? 0.4 : 1 }}>
        <button type="button" className="causa-efecto__agarre" ref={setActivatorNodeRef}
            {...attributes} {...listeners} aria-label={`Arrastrar causa ${item.id}`}>⠿</button>
        <button type="button" className="causa-efecto__seleccion" aria-pressed={selected} onClick={onSelect}>
            {item.id.length === 1 ? `${item.id}) ` : ''}{item.content}
        </button>
    </div>;
};

const DroppableEffect = ({ target, source, onAssign, onClear }) => {
    const { setNodeRef, isOver } = useDroppable({ id: target.id });
    return <div ref={setNodeRef} className={`causa-efecto__destino ${isOver ? 'sobre' : ''}`}>
        <button type="button" className="causa-efecto__asignar" onClick={onAssign}
            aria-label={`Asignar causa a ${target.content}`}>
            <strong>{target.content.replace(/^\(\s*\)\s*/, '')}</strong>
            <span>{source ? `${source.id.length === 1 ? source.id + ') ' : ''}${source.content}` : 'Suelta una causa aquí o selecciónala y toca este efecto.'}</span>
        </button>
        {source && <button type="button" className="causa-efecto__quitar" onClick={onClear}
            aria-label={`Quitar causa de ${target.content}`}>Quitar</button>}
    </div>;
};

const MatchingBoard = ({ sources, targets, onAssign }) => {
    const sensors = useDragSensors();
    const [selected, setSelected] = useState('');
    const [activeId, setActiveId] = useState(null);
    const active = sources.find(item => item.id === activeId);
    const assign = (sourceId, targetId) => {
        if (!sources.some(item => item.id === sourceId) || !targets.some(item => item.id === targetId)) return;
        onAssign(sourceId, targetId);
        setSelected('');
    };
    return <DndContext sensors={sensors} collisionDetection={args => {
        const hits = pointerWithin(args);
        return hits.length ? hits : rectIntersection(args);
    }} onDragStart={({ active }) => setActiveId(active.id)} onDragCancel={() => setActiveId(null)}
        onDragEnd={({ active, over }) => { setActiveId(null); if (over) assign(active.id, over.id); }}>
        <p>Arrastra desde el asa ⠿. En móvil, mantenla pulsada un instante antes de moverla. También puedes tocar una causa y luego su efecto.</p>
        <Row>
            <Col md={6}><h5>Causas posibles</h5>
                {sources.map(item => <DraggableCause key={item.id} item={item} selected={selected === item.id} onSelect={() => setSelected(item.id)} />)}
            </Col>
            <Col md={6}><h5>Efectos correspondientes</h5>
                {targets.map(target => <DroppableEffect key={target.id} target={target}
                    source={sources.find(item => item.id === target.letter)}
                    onAssign={() => selected && assign(selected, target.id)} onClear={() => onAssign('', target.id)} />)}
            </Col>
        </Row>
        <p role="status" className="small">{selected ? `Causa seleccionada: ${sources.find(item => item.id === selected)?.content}. Toca el efecto correspondiente.` : 'Selecciona o arrastra una causa.'}</p>
        {createPortal(<DragOverlay zIndex={2000}>{active ? <div className="causa-efecto__flotante">{active.content}</div> : null}</DragOverlay>, document.body)}
    </DndContext>;
};

const SortableItem = ({ item, index, total, onMove }) => {
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
    return <li ref={setNodeRef} className="causa-efecto__orden" style={{ transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 1 : 'auto', position: 'relative' }}>
        <button type="button" ref={setActivatorNodeRef} className="causa-efecto__agarre" {...attributes} {...listeners}
            aria-label={`Arrastrar secuencia ${index + 1}`}>⠿</button>
        <span>{item.content}</span>
        <div className="causa-efecto__mover">
            <Button size="sm" variant="outline-secondary" disabled={index === 0} onClick={() => onMove(index, index - 1)} aria-label={`Subir secuencia ${index + 1}`}>Subir</Button>
            <Button size="sm" variant="outline-secondary" disabled={index === total - 1} onClick={() => onMove(index, index + 1)} aria-label={`Bajar secuencia ${index + 1}`}>Bajar</Button>
        </div>
    </li>;
};

const M7_Ejercicio7 = ({ cerrarModal }) => {
    const { currentUser } = useContext(AppContext);
    const modoConsulta = useModoConsultaEvaluacion();
    const [step, setStep] = useState(1); // Paso actual
    const [causa, setCausa] = useState('');
    const [efecto, setEfecto] = useState('');
    const [selectedOption, setSelectedOption] = useState('');
    const [selectedEffect, setSelectedEffect] = useState('');
    const [enviando, setEnviando] = useState(false);
    const sortSensors = useDragSensors(true);

    const handleOptionChange = (option) => {
        setSelectedOption(option);
    };

    // const [respuestaLibre, setRespuestaLibre] = useState('');
    // const [open, setOpen] = useState(false);

    // Datos ejercicio 1
    const [items] = useState([
        { id: 'a', content: 'Dio a luz hermosa niña' },
        { id: 'b', content: 'Niña pequeña y débil' },
        { id: 'c', content: 'Todos querían ser el primero' },
        { id: 'd', content: 'Se enteró de la triste noticia' },
        { id: 'e', content: 'Sintió miedo' }
    ]);

    const [targets, setTargets] = useState([
        { id: '1', content: '(  ) la jarra se cayó', letter: '' },
        { id: '2', content: '(  ) se escondió', letter: '' },
        { id: '3', content: '(  ) alegría', letter: '' },
        { id: '4', content: '(  ) la bautizaron en casa', letter: '' },
        { id: '5', content: '(  ) decidió ir a buscarlos', letter: '' }
    ]);

    // Datos del ejercicio 2
    const [causes] = useState([{ id: 'cause-1', content: 'El padre maldice a los hijos' }]);
    const [effects] = useState([
        { id: 'effect-1', content: 'a) Se quedaron sin agua' },
        { id: 'effect-2', content: 'b) Se oyó un trueno poderoso' },
        { id: 'effect-3', content: 'c) Los hijos se convierten en cuervos' }
    ]);

    const  secuencia = [
    {key: 'a', text: 'Padre de impacienca, sale asustado, maldice a los hijos'},
    {key: 'b', text: 'Mete el hueso en la cerradura, oye chirridos, puede entrar.'},
    {key: 'c', text: 'Oye el deseo del pajaro, besa al hermano, vuelven a ser ninos.'},
];

const [blocks, setBlocks] = useState([
    {
        id: 'block-1',
        title: 'Ordena los hechos de cada secuencia de causa y efecto',
        items: [
            { id: 'item-1-1', content: 'a) Niños convertidos en cuervos, padres tristes, maldición del papá.' },
            { id: 'item-1-2', content: 'b) Nina enferma, se rompio la jarra, todos querían ser el primero, padre envio a la fuente a buscar agua' },
            { id: 'item-1-3', content: 'c) Nina oye deseo, cuervo expresa deseo, nina besa hermanos, cuervos vuelven a ser ninos.' }
            
        ]
    }
]);

    const assignCause = (sourceId, targetId) => setTargets(prev => prev.map(target =>
        target.id === targetId ? { ...target, letter: sourceId }
            : sourceId && target.letter === sourceId ? { ...target, letter: '' } : target
    ));

    const pasoCompleto = () => {
        if (step === 2) return targets.every(target => target.letter);
        if (step === 3) return Boolean(selectedEffect);
        if (step === 4) return causa.trim() && efecto.trim();
        if (step === 5) return Boolean(selectedOption);
        return true;
    };
    const avisarIncompleto = () => Swal.fire({ icon: 'warning', title: 'Campos incompletos', text: 'Completa las respuestas de este paso antes de continuar.', confirmButtonText: 'Aceptar' });

    const enviarResultados = async () => {
        if (modoConsulta) { cerrarModal?.(); return; }

        if (enviando) return;
        if (!targets.every(target => target.letter) || !selectedEffect || !causa.trim() || !efecto.trim() || !selectedOption) {
            avisarIncompleto();
            return;
        }
        const compiledResults = targets.reduce((acc, target) => {
            const item = items.find(i => i.id === target.letter);
            if (item) {
                acc[target.content] = item.content;
            }
            return acc;
        }, {});

        setEnviando(true);
        try {
            await addDoc(collection(db, "evaluaciones"), {
                userId: currentUser.uid,
                nombre: "Ejercicio 7: Causa y efecto",
                nivel: "Intermedio",
                modulo: "7",
                tipo: "Causa y efecto",
                createdAt: new Date(),
                respuestas: {
                    ...compiledResults,
                    efectoSeleccionado: effects.find(item => item.id === selectedEffect).content,
                    causa, efecto, secuencia: selectedOption,
                    orden: blocks[0].items.map(item => item.content)
                }
            });
            Swal.fire({
                title: '¡Bien hecho!',
                text: 'Evaluación finalizada con éxito.',
                icon: 'success',
                confirmButtonColor: '#3085d6',
                confirmButtonText: 'Excelente'
            }).then((result) => {
                if (result.isConfirmed) {
                    cerrarModal();  // Cierra la modal al confirmar la alerta
                }
            });
        } catch (error) {
            console.error("Error al enviar la evaluación: ", error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Error al enviar la evaluación.',
                confirmButtonColor: '#3085d6',
                confirmButtonText: 'Aceptar'
            });
        } finally {
            setEnviando(false);
        }
    };

    const advanceStep = () => {
        if (!modoConsulta && (!pasoCompleto())) { avisarIncompleto(); return; }
        setStep((prev) => prev + 1);
    };

    const prevStep = () => {
        setStep((prev) => Math.max(prev - 1, 1));
    };

    // Drag and drop para listas simples (ordenar)
    const handleListDragEnd = (event, items, setItems) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = items.findIndex(i => i.id === active.id);
        const newIndex = items.findIndex(i => i.id === over.id);
        if (oldIndex === -1 || newIndex === -1) return;
        setItems(arrayMove(items, oldIndex, newIndex));
    };

    return (
        <Container className="mt-4 causa-efecto">
            <h2>  EJERCICIO 7: CAUSA Y EFECTO</h2>
            {step === 1 && (
                <div>

                    <p>
                        Una de las habilidades que nos permiten comprender mejor un texto es, distinguir en una relación de
                        hechos, cuál causa otro. ¿Cuáles hechos son consecuencias de otros? Un hecho es causante cuando
                        constituye la razón por la qué algo sucedió. Y el efecto es el resultado de ésa causa.
                    </p>
                    <p>
                        En una lectura podemos encontrar varias secuencias causa-efecto-causa-efecto; en donde un efecto se
                        convierte en la causa de otro y asi sucesivamente. Diremos, por ejemplo, la lechera caminaba
                        distraídamente (causa) y se cayó (efecto). al caerse (causa) se le rompió el cántaro (efecto), y se le regó la
                        leche (efecto del rompimiento del cantaro), Iloro desconsolada (efecto final de toda la cadena de
                        hechos)
                    </p><p>
                        La habilidad para diferenciar causas de efectos se produce en el nivel de interpretación: donde el lector
                        analiza el papel que juega la composicion de las frases dentro de la lectura. De diferentes maneras.
                        unas frases son complemento de otras.
                    </p><p>
                        En los ejercicios siguientes usted establecerá las relaciones causa-efecto de la lectura de este módulo.
                    </p><p className='text-center'>
                        <strong>Lectura: Los Siete Cuervos</strong>
                    </p>
                    <p>
                        1. Relacione las causas con los efectos, colocando entre el parentesis al lado de cada efecto, la letra que
                        corresponda a la causa.
                    </p>
                    <Button onClick={advanceStep}>Iniciar Ejercicio</Button>
                </div>
            )}
            {step === 2 && (
                <div>
                    <MatchingBoard sources={items} targets={targets} onAssign={assignCause} />
                    <div className="d-flex gap-2 mt-3"><Button variant="secondary" onClick={prevStep}>Volver</Button><Button onClick={advanceStep}>Siguiente Ejercicio</Button></div>
                </div>
            )}
            {step === 3 && (
                <div>
                    <p>Relaciona la causa con el efecto que le corresponde.</p>
                    <MatchingBoard sources={causes} targets={effects.map(item => ({ ...item, letter: selectedEffect === item.id ? causes[0].id : '' }))}
                        onAssign={(sourceId, targetId) => setSelectedEffect(sourceId ? targetId : '')} />
                    <div className="d-flex gap-2 mt-3"><Button variant="secondary" onClick={prevStep}>Volver</Button><Button onClick={advanceStep}>Siguiente Ejercicio</Button></div>
                </div>
            )}
            {step === 4 && (
                <div>
                    <p>Ella dudó al principio de lo que le decían, porque le parecía muy raro</p>
                    <Form onSubmit={event => { event.preventDefault(); advanceStep(); }}>
                        <FormGroup className="mb-3">
                            <FormLabel htmlFor="causaInput">Causa</FormLabel>
                            <div className="text-muted small mb-1">Escriba la causa aquí...</div>
                            <FormControl
                                type="text"
                                name="causa"
                                id="causaInput"
                                value={causa}
                                onChange={e => setCausa(e.target.value)}
                                style={{ width: '100%' }}
                            />
                        </FormGroup>
                        <FormGroup className="mb-3">
                            <FormLabel htmlFor="efectoInput">Efecto</FormLabel>
                            <div className="text-muted small mb-1">Escriba el efecto aquí...</div>
                            <FormControl
                                type="text"
                                name="efecto"
                                id="efectoInput"
                                value={efecto}
                                onChange={e => setEfecto(e.target.value)}
                                style={{ width: '100%' }}
                            />
                        </FormGroup>
                        <div className="d-flex gap-2 mt-3">
                            <Button variant="secondary" type="button" onClick={prevStep}>Volver</Button>
                            <Button type="button" onClick={() => advanceStep()}>Siguiente Ejercicio</Button>
                        </div>
                    </Form>
                </div>
            )}
            {step === 5 && (
                <div>
                    <h5>Ejercicio 7</h5>
                    {secuencia.map(option => (
                        <div key={option.key}>
                            <label>
                                <input
                                    type="radio"
                                    name="criteriosCultura"
                                    checked={selectedOption === option.key}
                                    onChange={() => handleOptionChange(option.key)}
                                />
                                {option.text}
                            </label>
                        </div>
                    ))}
                    <div className="d-flex gap-2 mt-3">
                        <Button variant="secondary" onClick={prevStep}>Volver</Button>
                        <Button onClick={() => advanceStep()}>Siguiente Ejercicio</Button>
                    </div>
                </div>
            )}
            {step === 6 && (
                <div>
                    {blocks.map(block => (
                        <div key={block.id}>
                            <h3>{block.title}</h3>
                            <DndContext sensors={sortSensors} collisionDetection={closestCenter} onDragEnd={event => handleListDragEnd(event, block.items, (newItems) => {
                                setBlocks(blocks.map(b => b.id === block.id ? { ...b, items: newItems } : b));
                            })}>
                                <SortableContext items={block.items.map(i => i.id)} strategy={verticalListSortingStrategy}>
                                    <ol className="causa-efecto__lista">
                                        {block.items.map((item, index) => <SortableItem key={item.id} item={item} index={index} total={block.items.length}
                                            onMove={(from, to) => setBlocks(prev => prev.map(b => b.id === block.id ? { ...b, items: arrayMove(b.items, from, to) } : b))} />)}
                                    </ol>
                                </SortableContext>
                            </DndContext>
                        </div>
                    ))}
                    <div className="d-flex gap-2 mt-3">
                        <Button variant="secondary" onClick={prevStep}>Volver</Button>
                        <Button disabled={enviando} onClick={enviarResultados} variant="primary">Finalizar Ejercicio</Button>
                    </div>
                </div>
            )}
        </Container>
    );
};

export default M7_Ejercicio7;


